import cx from 'classnames';
import { delay, identity, isNil, union } from 'lodash';
import { Component, createRef } from 'react';
import { MultiSelectType } from 'models/enum';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSDropDownSearch, ELSTagGroup } from 'components/common/els';
import './multi.select.scss';

export interface MultiselectDropdownEFProps {
  chartFormField: ChartMetaFormField;
  disableEmptyDropdown?: boolean;
  isDisabled?: boolean;
  value?: string;
  selectType?: MultiSelectType;
  ariaRequired?: boolean;
  placeholder?: string;
  showSearchIcon?: boolean;
  disableSearch?: boolean;
  showActionBar?: boolean;
}

interface MultiselectDropdownEFState {
  disableEmptyDropdown: boolean;
  displayValue: string;
  isOpen: boolean;
  options: ContentItem[];
  reset: number;
  search: string;
  selectedOptionNames: string[];
}
class MultiselectDropdownEF extends Component<MultiselectDropdownEFProps, MultiselectDropdownEFState> {
  static displayName = 'MultiselectDropdownEF';
  static defaultProps = {
    disableEmptyDropdown: false,
    placeholder: 'Search or Select',
    showSearchIcon: true,
    disableSearch: false,
    showActionBar: false
  };

  isUnmount = false;

  containerRef = createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      disableEmptyDropdown: false,
      displayValue: this.getSelectedLabel(),
      isOpen: false,
      options: this.getFilteredOptions(''),
      reset: 0,
      search: '',
      selectedOptionNames: union(this.getSelectedOptionNames(), this.getSelectedValues())
    };
  }

  componentDidMount() {
    this.applySearchLock();
    document.addEventListener('mousedown', this.handleOutsideMouseDown);
  }

  componentDidUpdate(prevProps) {
    const { chartFormField, selectType } = this.props;
    if (chartFormField !== prevProps.chartFormField) {
      if (chartFormField.chartContent) {
        this.setState((prevState) => {
          let { search, reset } = prevState;
          let options = this.getFilteredOptions(search);
          if (
            this.isChartContentChangedByVisibility(chartFormField.chartContent, prevProps.chartFormField.chartContent) ||
            selectType === MultiSelectType.SINGLE ||
            chartFormField.disabled
          ) {
            reset += 1;
            search = '';
            options = this.getFilteredOptions(search);
          }

          return {
            disableEmptyDropdown: this.checkToDisableEmptyDropdown(),
            displayValue: this.getSelectedLabel(),
            options,
            reset,
            search,
            selectedOptionNames: this.getSelectedOptionNames()
          };
        });
      } else {
        // In case the chartFormField is changed by reset form action (invoking initState function)
        this.setState((prevState) => ({ disableEmptyDropdown: true, options: [], reset: prevState.reset + 1, search: '', selectedOptionNames: [] }));
      }
    }
    this.applySearchLock();
  }

  componentWillUnmount() {
    this.isUnmount = true;
    document.removeEventListener('mousedown', this.handleOutsideMouseDown);
  }

  // ELSDropDownSearch always renders a typable text input. When disableSearch is set we lock that
  // input to read-only so the control behaves as a click-only dropdown (no text entry / filtering).
  applySearchLock = () => {
    if (!this.props.disableSearch) return;
    const input = this.containerRef.current?.querySelector('input');
    if (input) {
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
    }
  };

  isChartContentChangedByVisibility = (chartContent: ContentItem[], otherChartContent: ContentItem[]) => {
    if (isNil(chartContent) || isNil(otherChartContent)) {
      return false;
    }
    if (chartContent.length !== otherChartContent.length) {
      return true;
    }
    const found = chartContent.find((item) => {
      return !otherChartContent.find((otherItem) => item.value === otherItem.value && item.isHidden === otherItem.isHidden);
    });

    return !!found;
  };

  handleOptionSelect = (newSelectedOption: ContentItem) => {
    const onChange = this.props.chartFormField.chartContent?.[0]?.onChange ?? identity;
    const isUserSelection = !isNil(newSelectedOption?.id ?? newSelectedOption?.selected);

    // Work-around to detect whether this is triggered by blur event
    if (!isUserSelection) {
      // 1. Since onSearchChangeHandler is delayed 300ms (core component),
      // there is a possibility of displayValue is not the latest so we need to delay here also
      // 2. In this case, do not use object destructuring for this.props.chartFormField
      delay(() => {
        if (!this.isUnmount) {
          if (this.state.displayValue === '') {
            // Clear selection
            onChange(this.props.chartFormField, { id: '' });
          } else {
            // Restore displayValue to the current selection
            this.setState({ search: '', displayValue: this.getSelectedLabel(), options: this.getFilteredOptions('') });
          }
        }
      }, 300);
    } else {
      onChange(this.props.chartFormField, newSelectedOption);
      if (this.props.showActionBar) {
        this.setState({ isOpen: true });
      }
    }
    const { changeCallback } = this.props.chartFormField;
    if (changeCallback) {
      changeCallback({ formField: this.props.chartFormField, option: newSelectedOption });
    }
  };

  handleSearchChange = (value: string) => {
    const options = this.getFilteredOptions(value);
    this.setState({
      displayValue: value,
      search: value,
      options
    });
  };

  handleTagDelete = (index: number) => {
    const changedOption = this.props.chartFormField.chartContent.filter((option) => option.selected)[index];
    changedOption.onChange(this.props.chartFormField, changedOption);
  };

  handleContainerFocus = () => {
    this.setState({ isOpen: true });
  };

  handleOutsideMouseDown = (event: MouseEvent) => {
    if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
      this.setState({ isOpen: false });
    }
  };

  handleActionBarResetMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const onChange = this.props.chartFormField.chartContent?.[0]?.onChange;
    if (!onChange) return;
    this.props.chartFormField.chartContent.filter((item) => item.selected).forEach((item) => onChange(this.props.chartFormField, item));
  };

  handleActionBarDoneMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isOpen: false });
  };

  getSelectedOptionNames = (): string[] => {
    return this.props.chartFormField.chartContent?.filter((option) => option.selected && !option.isHidden).map((option) => option.name) ?? [];
  };

  getSelectedValues = (): string[] => {
    return this.props.chartFormField.value?.map?.((item) => item.value) ?? [];
  };

  getSelectedOptionLabels = (): string[] => this.props.chartFormField.chartContent?.filter((option) => option.selected).map((option) => option.label) ?? [];

  getFilteredOptions = (search: string): ContentItem[] => {
    return this.props.chartFormField.chartContent?.filter((item) => !item.isHidden && item.name.toLowerCase().includes(search.toLowerCase())) ?? [];
  };

  getSelectedLabel = (): string => {
    const data: string[] = this.getSelectedOptionLabels();
    if (data.length === 0) {
      return '';
    }
    return data[0];
  };

  checkToDisableEmptyDropdown = () => {
    return this.props.disableEmptyDropdown && this.props.chartFormField.chartContent?.filter((item) => !item.isHidden).length === 0;
  };

  render() {
    const { disabled, name } = this.props.chartFormField;
    const { isDisabled, ariaRequired, showActionBar } = this.props;
    const { disableEmptyDropdown, options, reset, displayValue, selectedOptionNames, isOpen } = this.state;
    const shouldDisable = isDisabled || disabled || disableEmptyDropdown;
    if (this.props.selectType === MultiSelectType.SINGLE) {
      return (
        <div ref={this.containerRef} className={cx('sng-multi-select-search', name, { 'sng-multi-select-search--no-search': this.props.disableSearch })}>
          <ELSDropDownSearch
            className="sng-multi-select-search__dropdown"
            disableAutoComplete
            id={name}
            isDisabled={shouldDisable}
            name={name}
            onOptionSelected={this.handleOptionSelect}
            onSearchChangeHandler={this.handleSearchChange}
            options={options}
            placeholder={this.props.placeholder}
            showSearchIcon
            type="normal"
            value={displayValue}
            ariaLabel={this.props.chartFormField.label}
            ariaRequired={ariaRequired}
          />
        </div>
      );
    }
    return (
      <div
        ref={this.containerRef}
        className={cx('sng-multi-select-search', name, {
          'sng-multi-select-search--no-search': this.props.disableSearch,
          'sng-multi-select-search--action-bar-open': showActionBar && isOpen
        })}
        onFocus={this.handleContainerFocus}
      >
        <span className="u-els-hide-visually" aria-live="polite">{`${options.length} items in list`}</span>
        <ELSDropDownSearch
          className="sng-multi-select-search__dropdown"
          disableAutoComplete
          id={name}
          isDisabled={shouldDisable}
          key={reset}
          name={name}
          onOptionSelected={this.handleOptionSelect}
          onSearchChangeHandler={this.handleSearchChange}
          options={options}
          placeholder={this.props.placeholder}
          showSearchIcon={this.props.showSearchIcon}
          type="checkbox"
          ariaRequired={ariaRequired}
        />
        {showActionBar && isOpen && (
          <div className="sng-multi-select-search__action-bar">
            <button type="button" className="sng-multi-select-search__action-button sng-multi-select-search__action-reset" onMouseDown={this.handleActionBarResetMouseDown}>
              Reset
            </button>
            <button type="button" className="sng-multi-select-search__action-button sng-multi-select-search__action-done" onMouseDown={this.handleActionBarDoneMouseDown}>
              Done
            </button>
          </div>
        )}
        {!!selectedOptionNames.length && (
          <div className="u-els-margin-top">
            <ELSTagGroup
              isLargerChipText
              isTagDeletable={!this.props.isDisabled}
              cssModifier="sng-multi-select-search__tag-name-group"
              tags={selectedOptionNames}
              onDeleteTag={this.handleTagDelete}
            />
          </div>
        )}
      </div>
    );
  }
}

export default MultiselectDropdownEF;
